/**
 * Componente PontoDesviosTable
 * 
 * Tabela responsiva para exibir desvios de ponto recentes.
 * Em mobile, exibe como cards. Em desktop, exibe como tabela.
 * 
 * @module components/Charts/PontoDesviosTable
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FaClock, FaCalendarAlt, FaCheckCircle, FaUser, FaUserInjured } from 'react-icons/fa';
import moment from 'moment';

/**
 * Componente de Tabela/Lista para exibir os desvios de ponto recentes.
 * 
 * @param {Array<Object>} desvios - Lista de objetos de desvio de ponto.
 * @param {string} [className] - Classes CSS adicionais
 * 
 * @returns {JSX.Element} Componente de tabela/cards responsivo
 */
const PontoDesviosTable = ({ desvios, className = '' }) => {
    if (!desvios || desvios.length === 0) {
        return (
            <div className={`h-40 flex items-center justify-center text-gray-500 text-sm ${className}`} role="status" aria-live="polite">
                <FaCheckCircle className="mr-2 text-green-500" aria-hidden="true" />
                <span>Nenhum desvio de ponto significativo encontrado nos últimos 7 dias.</span>
            </div>
        );
    }

    // Função para formatar a data/hora
    const formatDateTime = (dateString) => {
        return moment(dateString).format('DD/MM HH:mm');
    };

    return (
        <div className={className}>
            {/* Versão Desktop: Tabela */}
            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200" role="table" aria-label="Tabela de desvios de ponto">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Profissional
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                                Paciente
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Previsto
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Check-in
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-red-600 uppercase tracking-wider">
                                Atraso
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {desvios.map((desvio) => (
                            <tr 
                                key={desvio.registro_ponto_id} 
                                className="hover:bg-red-50/50 transition-colors"
                                role="row"
                            >
                                <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {desvio.profissional_nome}
                                </td>
                                <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
                                    {desvio.paciente_nome || "N/A"}
                                </td>
                                <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                                    {formatDateTime(desvio.agendamento_previsto)}
                                </td>
                                <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-yellow-700 font-semibold">
                                    {formatDateTime(desvio.checkin_realizado)}
                                </td>
                                <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm font-bold text-red-600">
                                    {desvio.minutos_atraso} min
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Versão Mobile: Cards */}
            <div className="md:hidden space-y-3" role="list" aria-label="Lista de desvios de ponto">
                {desvios.map((desvio) => (
                    <div
                        key={desvio.registro_ponto_id}
                        className="bg-white border border-red-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                        role="listitem"
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <FaUser className="text-gray-400" size={14} aria-hidden="true" />
                                    <span className="text-sm font-semibold text-gray-900">
                                        {desvio.profissional_nome}
                                    </span>
                                </div>
                                {desvio.paciente_nome && (
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <FaUserInjured className="text-gray-400" size={12} aria-hidden="true" />
                                        <span>{desvio.paciente_nome}</span>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-1 bg-red-100 px-2 py-1 rounded">
                                <FaClock className="text-red-600" size={12} aria-hidden="true" />
                                <span className="text-xs font-bold text-red-600">
                                    {desvio.minutos_atraso} min
                                </span>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-xs">
                            <div>
                                <div className="flex items-center gap-1 text-gray-500 mb-1">
                                    <FaCalendarAlt className="text-gray-400" size={10} aria-hidden="true" />
                                    <span className="font-medium">Previsto:</span>
                                </div>
                                <p className="text-gray-700 font-medium">
                                    {formatDateTime(desvio.agendamento_previsto)}
                                </p>
                            </div>
                            <div>
                                <div className="flex items-center gap-1 text-gray-500 mb-1">
                                    <FaClock className="text-yellow-600" size={10} aria-hidden="true" />
                                    <span className="font-medium">Check-in:</span>
                                </div>
                                <p className="text-yellow-700 font-semibold">
                                    {formatDateTime(desvio.checkin_realizado)}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

PontoDesviosTable.propTypes = {
    /** Lista de objetos de desvio de ponto */
    desvios: PropTypes.arrayOf(PropTypes.shape({
        registro_ponto_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        profissional_nome: PropTypes.string.isRequired,
        paciente_nome: PropTypes.string,
        agendamento_previsto: PropTypes.string.isRequired,
        checkin_realizado: PropTypes.string.isRequired,
        minutos_atraso: PropTypes.number.isRequired,
    })),
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
};

PontoDesviosTable.defaultProps = {
    desvios: [],
    className: '',
};

export default PontoDesviosTable;
